import React from "react";
import { MetadataAuditComparerRow } from "../../api/metadataAuditComparerApi";
import { classNames } from "primereact/utils";
import { Tag } from "primereact/tag";
import { TokenizedField } from "./MetadataAuditComparer.types";

const TOKEN_RELABELS: Record<string, string> = {
  blocker: "Blocker",
  validation: "Validation",
  hygiene: "Hygiene",
  missing_internal_work_code: "UMPG Missing",
  iswc_delayed: "ISWC Delayed",
  title_mismatch: "Title Mismatch",
  title_conflict: "Title Conflict",
  writer_mismatch: "Writer Mismatch",
  iswc_mismatch: "ISWC Mismatch",
  missing_mc_title: "Missing MC Title",
  missing_comp_title: "Missing Comp Title",
  missing_sr_title: "Missing SR Title",
  missing_writer: "Missing Writer",
  missing_iswc: "Missing ISWC",
  missing_isrc: "Missing ISRC",
  missing_mc_iswc: "Missing MC ISWC",
  missing_sr_asset_id: "Missing SR Asset ID",
  missing_comp_asset_id: "Missing Comp Asset ID",
  missing_brand: "Missing Brand",
  missing_sr_artist: "Missing SR Artist",
  artist_brand_mismatch: "Artist/Brand Mismatch",
  writer_diacritic: "Writer Diacritic",
  writer_initials: "Writer Initials",
  writer_spacing: "Writer Spacing",
  writer_punctuation: "Writer Punctuation",
  title_abbreviation: "Title Abbreviation",
  title_punctuation: "Title Punctuation",
  title_spacing: "Title Spacing",
  title_numeric_form: "Title Numeric Form",
};

const TOKEN_CLASSNAMES: Record<string, string> = {
  // Severity types
  blocker: "p-tag-red bg-red-300",
  validation: "p-tag-yellow bg-yellow-300",
  hygiene: "p-tag-green bg-green-300",

  // Blocker types
  missing_internal_work_code: "p-tag-pink bg-pink-300",
  missing_iswc: "p-tag-pink bg-pink-300",
  iswc_mismatch: "p-tag-pink bg-pink-300",

  // Delayed metadata types
  iswc_delayed: "p-tag-cyan bg-cyan-300",

  // Discrepancy types
  title_mismatch: "p-tag-blue bg-blue-300",
  title_conflict: "p-tag-blue bg-blue-200",
  writer_mismatch: "p-tag-blue bg-blue-300",
  artist_brand_mismatch: "p-tag-blue bg-blue-300",
  missing_mc_title: "p-tag-teal bg-teal-300",
  missing_comp_title: "p-tag-teal bg-teal-300",
  missing_sr_title: "p-tag-teal bg-teal-300",
  missing_writer: "p-tag-teal bg-teal-300",
  missing_sr_artist: "p-tag-teal bg-teal-300",
  missing_sr_asset_id: "p-tag-cyan bg-cyan-600",
  missing_comp_asset_id: "p-tag-cyan bg-cyan-600",
  missing_isrc: "p-tag-orange bg-orange-300",
  missing_mc_iswc: "p-tag-orange bg-orange-300",
  missing_brand: "p-tag-orange bg-orange-300",

  // Hygiene types
  writer_spacing: "p-tag-indigo bg-indigo-200",
  title_spacing: "p-tag-indigo bg-indigo-200",
  writer_punctuation: "p-tag-indigo bg-indigo-100",
  title_punctuation: "p-tag-indigo bg-indigo-100",
  writer_diacritic: "p-tag-purple bg-purple-300",
  writer_initials: "p-tag-indigo bg-indigo-300",
  title_abbreviation: "p-tag-purple bg-purple-100",
  title_numeric_form: "p-tag-purple bg-purple-200",
};

export const dataHygieneAlertBody = (rowData: MetadataAuditComparerRow) => {
  const value = rowData.DataHygieneAlert;
  return (
    <i
      className={classNames("pi text-2xl", {
        "pi-check-circle text-green-500": value === true,
        "pi-times-circle text-red-500": value === false,
      })}
    />
  );
};

export const manualReviewRequiredBody = (rowData: MetadataAuditComparerRow) => {
  const value = rowData.ManualReviewRequired;
  return (
    <i
      className={classNames("pi text-2xl", {
        "pi-check-circle text-green-500": value === true,
        "pi-times-circle text-red-500": value === false,
      })}
    />
  );
};

const parseIssues = (value?: unknown): string[] =>
  Array.isArray(value)
    ? value.map((v) => String(v).trim()).filter(Boolean)
    : String(value ?? "")
        .split(/\.(?=[A-Z(])/g)
        .map((s) => s.trim())
        .filter(Boolean);

const identifiedIssueTag = (issue: string): string => {
  if (/UMPG|ISWC are different/.test(issue)) return "p-tag-pink bg-pink-300";
  if (/delayed metadata/.test(issue.toLowerCase())) return "p-tag-cyan bg-cyan-300";
  if (/writer fields|Artist is missing|title is missing/.test(issue)) return "p-tag-teal bg-teal-300";
  if (
    /titles|itle variation|writers|values are inconsistent across related assets/.test(
      issue.toLowerCase(),
    )
  )
    return "p-tag-blue bg-blue-300";
  if (/conflict/.test(issue)) return "p-tag-blue bg-blue-200";
  if (/rand is missing|ISRC is/.test(issue)) return "p-tag-orange bg-orange-300";
  if (/Asset ID is/.test(issue)) return "p-tag-cyan bg-cyan-600";

  if (/spacing discrepancy/.test(issue)) return "p-tag-indigo bg-indigo-200";
  if (/diacritic discrepancy/.test(issue)) return "p-tag-purple bg-purple-300";
  if (/numeric form discrepancy/.test(issue)) return "p-tag-purple bg-purple-200";
  if (/initial expansion/.test(issue)) return "p-tag-indigo bg-indigo-300";
  if (/abbreviation discrepancy/.test(issue)) return "p-tag-purple bg-purple-100";
  if (/punctuation discrepancy/.test(issue)) return "p-tag-indigo bg-indigo-100";

  return "issue-tag";
};

export const identifiedIssuesBody = (rowData: MetadataAuditComparerRow) => {
  const issues = parseIssues(rowData.IdentifiedIssues);
  if (!issues.length) return null;
  return (
    <div className="issues-list">
      {issues.map((i, j) => (
        <Tag
          key={`identified-issues-${j}-${i}`}
          value={i}
          className={classNames(identifiedIssueTag(i))}
        />
      ))}
    </div>
  );
};

export const tokenBodyFor =
  (field: TokenizedField) => (rowData: MetadataAuditComparerRow) => {
    const tokens = toCellTokens(rowData[field]);
    if (!tokens.length) return null;

    return (
      <div className="token-container">
        {tokens.map((t, i) => (
          <Tag
            key={`${field}-${i}-${t}`}
            value={labelForToken(t)}
            className={TOKEN_CLASSNAMES[t]}
          />
        ))}
      </div>
    );
  };

export const toCellTokens = (value: unknown): string[] => {
  const raw = String(value ?? "").trim();
  if (!raw) return [];

  return raw
    .split(/[|,;]/g)
    .map((t) => t.trim())
    .filter(Boolean);
};

export const labelForToken = (token: string) => {
  return TOKEN_RELABELS[token] ?? token.replaceAll("_", " ");
};

export const severityRowClassNames = (rowData: MetadataAuditComparerRow) => {
  switch (rowData.DiscrepancySeverity) {
    case "hygiene":
      return "hygiene-severity";
    case "validation":
      return "validation-severity";
    case "blocker":
      return "blocker-severity";
    default:
      return "clean-severity";
  }
};

export const W = {
  xss: { width: "4rem", minWidth: "4rem", maxWidth: "4rem" },
  xs: { width: "5rem", minWidth: "5rem", maxWidth: "5rem" },
  s: { width: "6rem", minWidth: "6rem", maxWidth: "6rem" },
  ss: { width: "7rem", minWidth: "7rem", maxWidth: "7rem" },
  m: { width: "9rem", minWidth: "9rem", maxWidth: "9rem" },
  l: { width: "13rem", minWidth: "13rem" },
  xl: { width: "16rem", minWidth: "16rem" },
};

export const WRAP_PRELINE = {
  whiteSpace: "pre-line" as const,
  overflowWrap: "anywhere" as const,
  lineHeight: "1.2rem",
};

export const formatStringField = (value: unknown): string => {
  if (value == null) return "";

  if (Array.isArray(value)) {
    return value
      .map((v) => String(v).trim())
      .filter(Boolean)
      .join(", ");
  }
  const raw = String(value).trim();

  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed
        .map((v) => String(v).trim())
        .filter(Boolean)
        .join(", ");
    }
  } catch {
    // Fall through to string normalization
  }

  return raw
    .replace(/^\[/, "")
    .replace(/\]$/, "")
    .replace(/"/g, "")
    .replace(/'/g, "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .join(", ");
};